import { Interpolater } from "./mod/Interpolater.js";
import { MapView } from "./MapView.js";
import { RasterProj } from './RasterProj.js';
import { Handlers } from './Handlers.js';

let Main = function () {
  let mapbox_access_token = import.meta.env.VITE_MAPBOX_KEY;
  let google_access_token = import.meta.env.VITE_GOOGLE_KEY;
  let geolocation_api_key = import.meta.env.VITE_GEOLOCATION_KEY;
  this.interpolateTimeSpan = 1e3;
  this.gl = null;
  this.canvas = document.querySelector('#webglCanvas');
  this.messagesBox = document.querySelector('.messages');
  this.mapView = null;
  this.prevTime = null;
  this.prevScale = null;
  this.viewStatus = {
    drag: false,
    dragPrevPos: null, // canvas x y from upper left corner
    pinching: false,
    pinchPrevDistance: null,
    lastTapTime: 0,
    lastTapPos: null,
    zoomScale: 3e+2, // zoomMin >= zoomScale >= zoomMax
    targetLambdaPhiZoom: null,
    interpolater: null,
    currTileLevel: null,
  };
  this.layerSelectBox = document.querySelector('select#layer');
  this.selectedLayer = this.layerSelectBox.value;
  this.gotoSelectBox = document.querySelector('select#goto');
  this.selectedGoto = this.gotoSelectBox.value;
  this.showCitiesCheckBox = document.querySelector('input#showCities');
  this.displayCities = this.showCitiesCheckBox.checked;
  this.mylocationButton = document.querySelector('button#myLocation');
  this.zoominButton = document.querySelector('button#zoomin');
  this.zoomoutButton = document.querySelector('button#zoomout');
  this.titleText = document.querySelector('.title-text');
  this.currYear = new Date().getFullYear();
  this.mouseDragged = false;
  this.wheelTimer = null;
  this.panRate = 0.005; // how fast to pan when dragging, smaller is slower

  this.googleSessions = [];

  this.gotoLocations = [ 
    // [name, lambda, phi] // long, lat * 0.0174533
    ["New York", -1.2916483296432497, 0.7105983955635071], // -74.00596618652344 40.714271545410156
    ["Washington DC", -1.34406026074966, 0.6787546684457174], //  -77.009003 38.889931
    ["Cairo", 0.5454098659116745, 0.5246920949378968], // 31.249670028686523, 30.06262969970703
    ["São Paulo", -0.8139539907104493, -0.410981592402649], // -46.6361083984375, -23.547500610351562
    ["Tokyo", 2.438081347027588, 0.622899531175232],   // 35.68949890136719, 139.69171142578125
    ["Beijing", 2.0315158102371216, 0.6965175910552979], // 116.39723205566406, 39.907501220703125
    ["Paris", 0.040994310061287884, 0.8526532000442506], // 2.34879994392395, 48.85340881347656
    ["Sydney", 2.639066738523865, -0.5911057683494568], // 151.2073211669922, -33.86785125732422
    ["London", -0.0021945780565992, 0.8989938207687379], //  -0.12574000656604767 51.50852966308594
    ["Delhi", 1.3479443834213258, 0.500071093529129], // 77.23149108886719, 28.65195083618164
    ["Hub of Universe", -1.2402274606460573, 0.7392943680816652], // Boston: hub of universe  -71.05976867675781, 42.358428955078125
  ];

  this.gotoLocations.forEach((loc,ix) => {
    const option = document.createElement("option");
    option.text = loc[0];
    option.value = ix;
    this.gotoSelectBox.add(option);
  });


  this.zoomMin = 0.1;
  this.zoomStart = 3e+2;
  this.zoomMax = 3e+6;
  this.maxTileLevel = 22; // tile levels 0 to maxTileLevel
  this.ringRadius = 0.000001; // radius of flat center disk in radians // 0.00001

  this.rasterProj = null;
  this.debug = false; // "local", "red", false
  this.messageTimeoutID = setTimeout(() => { this.messagesBox.innerHTML = "Double click anywhere <br>to go there fast!" }, 180000);

  document.addEventListener('DOMContentLoaded', async () => {
    this.getQueryParams(); // check for url params

    let lam_phi = await this.getUsersLocation(false);
    if (lam_phi) {
      this.viewStatus.lam0 = lam_phi.lambda;
      this.viewStatus.phi0 = lam_phi.phi;
    }

    this.rasterProj = new RasterProj(this.ringRadius);
    this.setPanRate(this.viewStatus.zoomScale);
    this.rasterProj.setScale(this.viewStatus.zoomScale);
    await this.startup(this.rasterProj); // sets up this.canvas, webgl, and calls init
    requestAnimationFrame(this.renderOnceSync);
  });

  window.addEventListener('resize', () => {
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    this.mapView.resizeCanvas(this.canvas);
    // this.rasterProj.clear(this.canvas);
    this.mapView.renderSync(false, this.displayCities);
  });



  this.animation = () => {
    this.setTileLevel();

    if (this.viewStatus.interpolater != null) {
      let currTime = new Date().getTime();
      // Interpolater is running
      let currPos = this.viewStatus.interpolater.getPos(currTime);
      this.setProjCenter(currPos.lambda, currPos.phi);
      this.viewStatus.zoomScale = currPos.zoom;
      this.rasterProj.setScale(this.viewStatus.zoomScale);
      if (this.viewStatus.interpolater.isFinished()) { // Interpolater finished
                this.viewStatus.interpolater = null;
        this.setQueryParams();
        this.mapView.render(true, this.displayCities);
        setTimeout(() => {
          this.mapView.renderSync(true, this.displayCities);
        }, 300); // schedule a sync render because assets can load after the first render
      } else { // Interpolater still running, continue animation
                requestAnimationFrame(this.animation);
        this.mapView.render(false, this.displayCities);
      }
    } else if (this.viewStatus.targetLambdaPhiZoom != null) {
      // Start Interpolator, new lambda phi requested
      let currLambdaPhi = this.mapView.getProjCenter();
      let currLambdaPhiZoom = { lambda: currLambdaPhi.lambda, phi: currLambdaPhi.phi, zoom: this.viewStatus.zoomScale };
      let targLambdaPhiZoom = this.viewStatus.targetLambdaPhiZoom;
      this.viewStatus.interpolater = Interpolater.create(
        currLambdaPhiZoom,
        targLambdaPhiZoom,
        this.interpolateTimeSpan
      );
      this.viewStatus.targetLambdaPhiZoom = null;
            requestAnimationFrame(this.animation);
      this.mapView.render(false, this.displayCities);
    }

  };

  this.prerender = () => {
    this.setTileLevel();
    // zoom in/out
    if (this.prevScale != this.viewStatus.zoomScale) { // zooming, or first time thru
      this.rasterProj.setScale(this.viewStatus.zoomScale);
      this.rasterProj.setFlatRatio(this.ringRadius);
      this.prevScale = this.viewStatus.zoomScale;
    }
    this.setQueryParams();
  }

  this.renderOnce = () => {
    this.prerender();
    this.mapView.render(false, this.displayCities);
  };

  this.renderOnceSync = () => {
    this.prerender();
    this.mapView.renderSync(true, this.displayCities);
  };

  this.startup = async (rasterProj) => {
    this.gl = this.canvas.getContext("webgl2");
    if (!this.gl) {
      return void alert("Failed to setup WebGL.");
    }

    this.canvas.addEventListener("webglcontextlost", this.handleContextLost.bind(this), false);
    this.canvas.addEventListener("webglcontextrestored", this.handleContextRestored.bind(this), false);
    this.layerSelectBox.addEventListener('change', this.handleLayerChange.bind(this), false);
    this.gotoSelectBox.addEventListener('change', this.handleGotoChange.bind(this), false); 
    this.showCitiesCheckBox.addEventListener('change', this.handleShowCitiesChange.bind(this), false);
    this.mylocationButton.addEventListener('click', this.handleMyLocationClick.bind(this), false);
    this.zoominButton.addEventListener('click', this.handleZoomInClick.bind(this), false);
    this.zoomoutButton.addEventListener('click', this.handleZoomOutClick.bind(this), false);
    this.titleText.addEventListener('click', this.handleTitleClick.bind(this), false);

    // Mouse and touch event listeners
    document.addEventListener("mousedown", this.handleMouseDown.bind(this), false);
    document.addEventListener("mousemove", this.handleMouseMove.bind(this), false);
    document.addEventListener("mouseup", this.handleMouseUp.bind(this), false);
    this.canvas.addEventListener("dblclick", this.handleDoubleClick.bind(this), false);

    this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this), false);
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this), false);
    this.canvas.addEventListener("touchend", this.handleTouchEnd.bind(this), false);

    window.WheelEvent && document.addEventListener("wheel", this.handleWheel.bind(this), false);

    document.addEventListener('keydown', this.handleKeydown.bind(this));

    this.googleSessions = await this.getSessions();
    await this.init(rasterProj);
  };

  this.init = async (rasterProj) => {
    let canvasInfo = this.canvas.getBoundingClientRect(); //read-only left, top, right, bottom, x, y, width, height properties of this.canvas
    let canvasSize = {
      width: canvasInfo.width,
      height: canvasInfo.height,
    };
    let tile_opts = {
      canvasSize: canvasSize,
    };
    let cache_opts = {
      num: 100,
      crossOrigin: false,
      debug: this.debug,
    };
    this.getQueryParams();
    rasterProj.init(this.gl);
    this.mapView = new MapView(this.gl, rasterProj, canvasSize, tile_opts, cache_opts);
    this.mapView.setProjCenter(this.viewStatus.lam0, this.viewStatus.phi0);

    await this.setLayer();

    this.setTileLevel();
  };

  this.setProjCenter = (lam0, phi0) => {
    if (lam0 != this.viewStatus.lam0 || phi0 !== this.viewStatus.phi0) {
      if (lam0 < -Math.PI) lam0 += (Math.PI * 2);
      if (lam0 > Math.PI) lam0 -= (Math.PI * 2);
      this.viewStatus.lam0 = lam0;
      this.viewStatus.phi0 = phi0;
      this.mapView.setProjCenter(lam0, phi0);
    }
  };

  this.setTileLevel = () => {
    this.viewStatus.currTileLevel = Math.round(Math.log10(this.viewStatus.zoomScale) * 3.0);// Math.floor(this.maxTileLevel * this.viewStatus.zoomScale / this.zoomMax);
    this.viewStatus.currTileLevel = Math.max(Math.min(this.viewStatus.currTileLevel, this.maxTileLevel), 0);
    // //console.log("TileLvl: " + this.viewStatus.currTileLevel," + ZoomScl: " + this.viewStatus.zoomScale);
    this.mapView.setTileLevel(this.viewStatus.currTileLevel);
  }

  // Set variables from query parameters in the URL
  // e.g. http://localhost:1234/?zoom=1000&lon=-74.0113949&lat=40.703355&map=mapbox_satellite&local&red
  this.getQueryParams = () => {
    let params = new URLSearchParams(window.location.search);
    let zoomScale = params.get("zoom");
    if (zoomScale) {
      zoomScale = parseFloat(zoomScale);
      if (!isNaN(zoomScale)) {
        this.viewStatus.zoomScale = Math.min(Math.max(this.zoomMin, zoomScale), this.zoomMax);
      }
    }
    let latitude = params.get("lat");
    let longitude = params.get("lon");
    if (latitude && longitude) {
      latitude = parseFloat(latitude);
      longitude = parseFloat(longitude);
      if (!isNaN(latitude) && !isNaN(longitude)) {
        this.viewStatus.lam0 = longitude * 0.0174533; //degrees to radians
        this.viewStatus.phi0 = latitude * 0.0174533; //degrees to radians
      }
    }
    let map = params.get("map");
    if (map) {
      this.selectedLayer = map;
    }
    let local = params.get("local");
    if (local == "") {
      this.debug = "local";
    }
    let red = params.get("red");
    if (red == "") {
      this.debug = "red";
    }
  };

  this.setQueryParams = () => {
    // //console.log(`Setting query params.${this.viewStatus.lam0}, ${this.viewStatus.phi0}, ${this.viewStatus.zoomScale}`);
    let params = new URLSearchParams(window.location.search);
    params.set("zoom", this.viewStatus.zoomScale.toFixed(2)); // zoomScale
    params.set("lon", (this.viewStatus.lam0 * 180 / Math.PI).toFixed(4)); // radians to degrees
    params.set("lat", (this.viewStatus.phi0 * 180 / Math.PI).toFixed(4)); // radians to degrees
    params.set("map", this.selectedLayer);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }

  this.getSessions = async () => { // Get Google Maps Tile API sessions for satellite, roadmap, terrain
    const language = navigator.language || "en-US";
    const locale = new Intl.Locale(language);
    const region = locale.region || "US";
    let google_body = {
      "language": language,
      "region": region,
    };

    google_body.layerTypes = ["layerRoadmap"];
    google_body.styles = [ // only used for roadmap
      {
        "featureType": "all",
        "elementType": "labels.text",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "road",
        "elementType": "labels.text",
        "stylers": [{ "visibility": "on" }]
      }
    ];
    let mapTypes = ["satellite", "roadmap"/*, "terrain"*/];
    let sessionKeys = [];
    for (let type of mapTypes) {
      google_body.mapType = type;
      // fetch session for each type
      const google_api_session = await fetch(`https://tile.googleapis.com/v1/createSession?key=${google_access_token}`, {
        method: "POST",
        body: JSON.stringify(google_body),
        headers: {
          "Content-Type": "application/json",
        }
      })
        .then(response => response.text())
        .catch(error => {
          console.error('Error fetching Google Maps Tile Session:', error);
        });
      const google_api_session_json = JSON.parse(google_api_session);
      sessionKeys.push(google_api_session_json.session);
    };
    return (sessionKeys);
  };

  this.setLayer = async () => { // which map layer to use
    let sessionKey = null;
    let attributionBox = document.querySelector('.attribution');
    if (this.selectedLayer === "mapbox_satellite") {
      attributionBox.innerHTML = `<span style="max-width: 50%">© <a href="https://www.mapbox.com/about/maps">Mapbox</a> 
        © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>
        © <a href="https://www.maxar.com/">Maxar</a><br>
        <strong><a href="https://apps.mapbox.com/feedback/" target="_blank">Improve this map</a></strong></span>`;
      this.mapView.setCityFontColor("white");
      this.mapView.getURL = function (z, x, y) { //Add custom function to MapView
        return `https://api.mapbox.com/v4/mapbox.satellite/${z}/${x}/${y}.png?access_token=${mapbox_access_token}`;
      }
    } else if (this.selectedLayer.slice(0, 6) === "google") { // google_satellite, google_roadmap
      attributionBox.innerHTML = `Map data @${this.currYear} Google`;
      if (this.selectedLayer === "google_satellite") {
        this.mapView.setCityFontColor("white");
        sessionKey = this.googleSessions[0];
      } else if (this.selectedLayer === "google_roadmap") {
        this.mapView.setCityFontColor("black");
        sessionKey = this.googleSessions[1];
      }

      this.mapView.getURL = function (z, x, y) { //Add custom function to MapView
        return `https://tile.googleapis.com/v1/2dtiles/${z}/${x}/${y}?session=${sessionKey}&key=${google_access_token}`;
      }
    }
  };

  this.getUsersLocation = async (getPrecise = false) => {
    if (!getPrecise) {
      // Use IP-based geolocation for a rough location (faster and doesn't require permission)
      try {
        const response = await fetch(`https://geolocation-db.com/json/${geolocation_api_key}`);
        const data = await response.json();
        return { lambda: data.longitude * 0.0174533, phi: data.latitude * 0.0174533 };
      } catch (error) {
        console.error('Error fetching IP-based geolocation:', error);
        return null;
      }
    } else if (navigator.geolocation) {
      // Use precise geolocation if permission is granted
      const { promise, resolve, reject } = Promise.withResolvers();
      let lam_phi =
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            resolve({ lambda: position.coords.longitude * 0.0174533, phi: position.coords.latitude * 0.0174533 });
          },
          (error) => {
            console.warn(`Geolocation error (${error.code}): ${error.message}`);
            reject(error);
          }
        );
      return promise;
    } else {
      console.warn("Geolocation is not supported by this browser.");
      return null;
    }
  };

  // Assign handler methods to this instance
  Object.assign(this, Handlers);
};

new Main();

export { Main };
